/**
 * Local Grade Processor
 * 
 * Client-side grade aggregation and processing.
 * Eliminates need for conversation-level Firestore writes.
 * 
 * Key benefits:
 * - Instant UI updates (no Firestore propagation delay)
 * - 60-80% reduction in Firestore writes
 * - Eliminates distributed state synchronization issues
 * - Works offline
 */

import {
  MajorityJudgment,
  Grade,
  GradeCounts,
  compareMajorityJudgments
} from './majorityJudgmentService';
import { getGradeRankings, resolveTestRetestGrades } from './gradeDescriptionsService';
import { GRADE_SCALE, countsFromGrades, majorityGrade, normalizeGrade } from './majorityJudgment';

/**
 * Raw grade record from backend (Firestore grades collection)
 */
export interface RawGrade {
  gradeId?: string; // Document ID from Firestore
  graderIdx?: number; // Optional: grader index in array
  graderModelId: string; // Model that gave the grade (or userId if human grader)
  candidateIdx?: number; // Optional: candidate index in array
  candidateModelId: string; // Model being graded (for peer) or synthesis variant (for self-eval) or userId (if human response)
  grade: string; // Letter grade: A, B, C, D, F
  isSelfEval?: boolean; // True if this is a self-eval grade
  isRetest?: boolean; // True if this is a test-retest grade
  timestamp: Date; // When the grade was created
  conversationId: string; // Conversation this grade belongs to
  userId?: string; // User who owns this conversation
  userPromptMessageId: string; // User prompt that triggered this grade
  gradingMessageId?: string; // Message ID that generated this grade (for progressive grading filtering)
  prioritySetting: string; // Priority setting (speed, balance, quality)
  creator?: string; // Creator/organization of the model (or userId if human)
  costUSD?: number; // Cost of the grading API call
  
  // +Human feature fields
  isHumanResponse?: boolean; // True if candidate is a human-written response
  humanResponseAuthor?: string; // userId of the human who wrote the response
  isHumanGrader?: boolean; // True if grader is a human (star rating)
  humanGraderId?: string; // userId of the human grader
  plusHumanOn?: boolean; // True if this grade was assigned during a +human round
  excludeFromGlobalLeaderboard?: boolean; // True if human opted out of global leaderboard
  
  // Metadata fields
  aisNumber?: number; // Number of AIs in the conversation
  gradingPromptVersion?: string; // Version of the grading prompt used
  ionoffVersion?: string; // Version of ionoff used
  promptWordCount?: number; // Word count of the user prompt
  reasoningSetting?: string; // Reasoning setting (default, low, high)
  responseWordCount?: number; // Word count of the model response
  selfevalPrehocSetting?: boolean; // Self-eval pre-hoc setting
  synthesisPromptVariant?: string; // Synthesis prompt variant
  synthesisPromptVersion?: string; // Version of synthesis prompt
  webGroundingSetting?: boolean; // Web grounding setting
  fallbackGrader?: boolean; // True if a fallback grader was used
  bestActualModelId?: string; // Actual modelId of "best" (peer-review winner) for tie-breaking
  ionoffEquals?: boolean; // True if this synthesis variant is the final ionoff
  isDuplicatedFromVariant?: string; // If this grade is a duplicate for ionoff version traceability, stores the original candidateModelId
  originalVariant?: string; // If this is a duplicated grade, stores the original synthesis variant name (e.g., 'plain', 'a-final')
  
  // Self-grading metadata
  isSelfGrade?: boolean; // True if grader graded its own response (diagonal in matrix)
}

/**
 * Model-level grade aggregation
 */
export interface ModelGradeAggregation {
  modelId: string;
  majorityJudgment: MajorityJudgment;
  grades: RawGrade[];
  lastUpdated: Date;
  prioritySetting: string;
  ionoffEquals?: boolean; // True if this synthesis variant is the final ionoff
}

/**
 * Creator-level grade aggregation
 */
export interface CreatorGradeAggregation {
  creatorPrefix: string;
  majorityJudgment: MajorityJudgment;
  grades: RawGrade[];
  lastUpdated: Date;
  prioritySetting: string;
}

/**
 * Priority-bucketed aggregations
 */
export interface PriorityBucket {
  modelGrades: Map<string, ModelGradeAggregation>;
  creatorGrades: Map<string, CreatorGradeAggregation>;
}

/**
 * Complete conversation-level grade state
 * 
 * Maintains 10 segregated buckets (5 priorities × 2 levels: model + creator)
 * PLUS a special combined bucket for "this conversation" display only
 */
export interface ConversationGradeState {
  conversationId: string; // Track which conversation this state belongs to
  
  // Segregated buckets by priority (for user/global aggregation & filtered display)
  buckets: {
    free: PriorityBucket;
    speed: PriorityBucket;
    balance: PriorityBucket;
    value: PriorityBucket;
    intelligence: PriorityBucket;
    custom: PriorityBucket;
  };
  
  // Combined bucket for "this conversation" display (cross-priority merge)
  // NEVER written to Firestore - display-only cache
  thisConversationCombined: {
  modelGrades: Map<string, ModelGradeAggregation>;
  creatorGrades: Map<string, CreatorGradeAggregation>;
  };
  
  // Self-eval grades (bucketed by priority)
  selfEvalGrades: {
    free: Map<string, ModelGradeAggregation>;
    speed: Map<string, ModelGradeAggregation>;
    balance: Map<string, ModelGradeAggregation>;
    value: Map<string, ModelGradeAggregation>;
    intelligence: Map<string, ModelGradeAggregation>;
    custom: Map<string, ModelGradeAggregation>;
  };
  
  // Combined self-eval for "this conversation" display
  selfEvalCombined: Map<string, ModelGradeAggregation>;
  
  rawGrades: RawGrade[]; // Flat array with prioritySetting field
  lastUpdated: Date;
}

/**
 * Local grade processor - runs entirely in-memory
 */
export class LocalGradeProcessor {
  
  /**
   * Calculate median of an array of numbers
   */
  private calculateMedian(values: number[]): number {
    if (values.length === 0) return 0;
    
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    
    if (sorted.length % 2 === 0) {
      return (sorted[mid - 1] + sorted[mid]) / 2;
    } else {
      return sorted[mid];
    }
  }
  
  /**
   * Build grade counts for Balinski gauge tie-breaking
   */
  private buildGradeCounts(grades: RawGrade[]): GradeCounts {
    // Legacy blends such as "A/C" count as their majority grade (the lower
    // letter); "+"/"-" suffixes are stripped; non-grades are ignored.
    return countsFromGrades(grades.map(g => g.grade));
  }

  /**
   * Rank of a grade for sorting: 0 = A (best) … 4 = F. Intrinsic to the
   * scale, not configurable. Unknown values sort as C.
   */
  private getGradeRank(grade: string): number {
    const letter = normalizeGrade(grade);
    return letter ? GRADE_SCALE.indexOf(letter) : 2;
  }
  
  /**
   * Consolidate test-retest grades so each grader contributes exactly once
   * 
   * In progressive grading, the same grader may grade the same receiver multiple times
   * across different rounds (test-retests). For fair ranking, each grader should contribute
   * only one grade, calculated as the K2 median of all their test-retests.
   * 
   * Example:
   *   Input: [
   *     { grader: 'model1', receiver: 'model2', grade: 'D', ... },
   *     { grader: 'model1', receiver: 'model2', grade: 'B', ... },
   *     { grader: 'model1', receiver: 'model2', grade: 'A', ... },
   *     { grader: 'model3', receiver: 'model2', grade: 'C', ... }
   *   ]
   *   Output: [
   *     { grader: 'model1', receiver: 'model2', grade: 'B', ... }, // K2 median of [D,B,A]
   *     { grader: 'model3', receiver: 'model2', grade: 'C', ... }
   *   ]
   * 
   * @param grades - Array of raw grades for a single receiver
   * @returns Array with exactly one grade per grader (consolidated via K2)
   */
  private consolidateTestRetests(grades: RawGrade[]): RawGrade[] {
    if (grades.length === 0) return grades;
    
    // Group grades by grader
    const gradesByGrader = new Map<string, RawGrade[]>();
    
    grades.forEach(grade => {
      const graderKey = grade.graderModelId;
      if (!gradesByGrader.has(graderKey)) {
        gradesByGrader.set(graderKey, []);
      }
      gradesByGrader.get(graderKey)!.push(grade);
    });
    
    // Consolidate each grader's grades
    const consolidatedGrades: RawGrade[] = [];
    
    gradesByGrader.forEach((graderGrades, graderModelId) => {
      if (graderGrades.length === 1) {
        // Single grade - no consolidation needed
        consolidatedGrades.push(graderGrades[0]);
      } else {
        // Multiple grades - apply K2 resolution
        const gradeLetters = graderGrades.map(g => g.grade);
        const consolidatedGrade = resolveTestRetestGrades(gradeLetters);
        
        // Create consolidated RawGrade object
        // Use the most recent grade as template, but update the grade letter
        const mostRecent = graderGrades.reduce((latest, current) => 
          current.timestamp > latest.timestamp ? current : latest
        );
        
        const consolidated: RawGrade = {
          ...mostRecent,
          grade: consolidatedGrade,
          // Mark as consolidated for debugging/tracking
          isRetest: true
        };
        
        consolidatedGrades.push(consolidated);
        
        console.log('🔄 Consolidated test-retests:', {
          grader: graderModelId.substring(0, 20),
          candidate: consolidated.candidateModelId.substring(0, 20),
          originalGrades: gradeLetters,
          consolidatedGrade,
          count: graderGrades.length
        });
      }
    });
    
    return consolidatedGrades;
  }
  
  /**
   * Calculate majority judgment from raw grades
   * Handles test-retest by taking median of repeated grades from same source
   */
  private calculateMajorityJudgment(
    grades: RawGrade[],
    uniquePromptIds: Set<string>
  ): MajorityJudgment {
    if (grades.length === 0) {
      return {
        medianGrade: 'C',
        judgmentsNumber: 0,
        promptsNumber: 0,
        gradeCounts: { A: 0, B: 0, C: 0, D: 0, F: 0 }
      };
    }
    
    // Step 1: Build grade counts (needed for Balinski median calculation)
    const gradeCounts = this.buildGradeCounts(grades);
    
    // Step 2: Calculate median using Balinski method (alphabetical ordering)
    // Import calculateMedianFromCounts from majorityJudgmentService
    // which uses proper alphabetical ordering: A < B < C < D < F
    const totalGrades = Object.values(gradeCounts).reduce((sum, count) => sum + count, 0);
    const medianGrade = this.calculateMedianFromCounts(gradeCounts, totalGrades) as Grade;
    
    // Step 3: Count total grades and unique prompts
    // judgmentsNumber = total number of grades (judgments)
    // promptsNumber = number of unique prompts
    
    // Debug logging for grade counts
    if (process.env.NODE_ENV === 'development' && grades.length > 0) {
      console.log('🔢 Majority judgment calculation:', {
        grades: grades.length,
        uniquePrompts: uniquePromptIds.size,
        promptIds: Array.from(uniquePromptIds),
      });
    }
    
    return {
      medianGrade,
      judgmentsNumber: grades.length, // Total number of grades/judgments for this model
      promptsNumber: uniquePromptIds.size, // Number of unique prompts
      gradeCounts
    };
  }
  
  /**
   * Calculate median grade from counts using Balinski method
   * This matches the backend's calculateMedianFromCounts logic
   * Uses alphabetical ordering: A < B < C < D < F
   */
  private calculateMedianFromCounts(counts: GradeCounts, _total: number): Grade {
    // Balinski–Laraki majority grade: the lower of the two middle grades
    // when the count is even, never a blend. Same code as the backend.
    return majorityGrade(counts) ?? 'C';
  }
  
  /**
   * Aggregate raw grades by model
   * @param messages - Optional messages array for message-index-based tie-breaking
   * @param gradeRankings - Optional K3 rankings from Firestore (for proper sorting)
   */
  aggregateByModel(
    grades: RawGrade[], 
    messages?: any[],
    gradeRankings?: Record<string, number>
  ): Map<string, ModelGradeAggregation> {
    const byModel = new Map<string, RawGrade[]>();
    
    // Group by candidate model
    grades.forEach(grade => {
      if (!byModel.has(grade.candidateModelId)) {
        byModel.set(grade.candidateModelId, []);
      }
      byModel.get(grade.candidateModelId)!.push(grade);
    });
    
    // Calculate majority judgment for each model
    const aggregations: Array<[string, ModelGradeAggregation]> = [];
    byModel.forEach((modelGrades, modelId) => {
      // CRITICAL FIX: For M|N counting, exclude version duplicate grades
      // These are grades created by duplicateSelfEvalGradesForIonoffVersion()
      // They're marked with isDuplicatedFromVariant field
      const gradesForCounting = modelGrades.filter(g => !g.isDuplicatedFromVariant);
      
      // CRITICAL: Consolidate test-retests so each grader contributes exactly once
      // In progressive grading, the same grader may grade the same receiver multiple times
      // We use K2 median resolution to get one grade per grader
      const consolidatedGrades = this.consolidateTestRetests(gradesForCounting);
      
      // Count unique prompts from consolidated grades
      const uniquePromptIds = new Set(consolidatedGrades.map(g => g.userPromptMessageId));
      
      // Calculate MJ using consolidated grades (one per grader)
      const mj = this.calculateMajorityJudgment(consolidatedGrades, uniquePromptIds);
      
      console.log('🔢 Model aggregation', {
        modelId: modelId.substring(0, 30),
        totalGrades: modelGrades.length,
        nonDuplicateGrades: gradesForCounting.length,
        consolidatedGrades: consolidatedGrades.length,
        testRetestsDetected: gradesForCounting.length > consolidatedGrades.length,
        uniquePrompts: uniquePromptIds.size,
        judgmentsNumber: mj.judgmentsNumber,
        promptsNumber: mj.promptsNumber
      });
      
      // Check if any grade has ionoffEquals flag
      const hasIonoffEquals = modelGrades.some(g => g.ionoffEquals === true);
      
      aggregations.push([
        modelId,
        {
        modelId,
        majorityJudgment: mj,
        grades: modelGrades, // Keep ALL grades for display (including duplicates)
        lastUpdated: new Date(),
        prioritySetting: modelGrades[0]?.prioritySetting || 'speed',
        ionoffEquals: hasIonoffEquals ? true : undefined
        }
      ]);
    });
    
    // Helper: Find message index for a model (earlier index = earlier response)
    const getMessageIndex = (modelId: string): number => {
      if (!messages) return 999999; // No messages, push to end
      
    // Map special IDs to what we search for in messages
    if (modelId.startsWith('ionoff-grading-')) {
      // Versioned ionoff ID from backend (e.g., ionoff-grading-v1-synthesis-v1-new-plain)
      // This is a duplicate of ionoff_new_plain (or whichever variant has ionoffEquals=true)
      // Map it to the same message index as the linked variant
      // Extract variant from ID: ionoff-grading-v1-synthesis-v1-new-plain -> new-plain
      const parts = modelId.split('-');
      const variant = parts.slice(5).join('-'); // Everything after "ionoff-grading-v1-synthesis-v1-"
      const idx = messages.findIndex(m => m.role === 'assistant' && m.synthesisVariant === variant);
      return idx >= 0 ? idx : 999999;
    } else if (modelId.startsWith('ionoff/ionoff-v')) {
      // For versioned ionoff IDs, look for synthesis with matching version
      const idx = messages.findIndex(m => m.role === 'assistant' && m.isSynthesized && !m.synthesisVariant);
      return idx >= 0 ? idx : 999999;
    } else if (modelId.startsWith('ionoff_')) {
      // For synthesis variants: extract variant name and convert underscores to hyphens
      // (Firestore stores "a-final", but we use "ionoff_a_final" as modelId)
      const variant = modelId
        .replace('ionoff_', '')
        .replace(/_/g, '-'); // Convert underscores back to hyphens for Firestore lookup
      const idx = messages.findIndex(m => m.role === 'assistant' && m.synthesisVariant === variant);
      return idx >= 0 ? idx : 999999;
      } else if (modelId === 'best') {
        // For 'best', try to find the actual peer-review winner's modelId
        // (stored in self-eval grade metadata by backend)
        const modelGrades = byModel.get('best');
        const bestActualModelId = modelGrades?.[0]?.bestActualModelId;
        
        if (bestActualModelId) {
          // Look for the actual winner's message
          const idx = messages.findIndex(m => 
            m.role === 'assistant' && 
            m.modelId === bestActualModelId && 
            !m.isSynthesized
          );
          if (idx >= 0) {
            console.log('🔍 Found "best" actual message', {
              bestActualModelId,
              messageIndex: idx
            });
            return idx;
          }
        }
        
        // Fallback: first model response (after user prompt)
        const idx = messages.findIndex(m => m.role === 'assistant' && m.modelId && !m.isSynthesized);
        return idx >= 0 ? idx : 999999;
      } else {
        // Check if this is a human response (userId as modelId)
        // Human messages have: isHumanResponse=true and humanUserId=<userId>
        const humanIdx = messages.findIndex(m => 
          m.role === 'assistant' && 
          m.isHumanResponse === true &&
          m.humanUserId === modelId
        );
        if (humanIdx >= 0) return humanIdx;
        
        // Regular model ID (AI models)
        const idx = messages.findIndex(m => m.role === 'assistant' && m.modelId === modelId);
        return idx >= 0 ? idx : 999999;
      }
    };
    
    // Sort by majority judgment, then by message index (earlier message wins ties)
    console.log('🔍 aggregateByModel: Sorting by MJ + message index', {
      hasMessages: !!messages,
      messageCount: messages?.length || 0,
      aggregationCount: aggregations.length
    });
    
    aggregations.sort(([idA, aggA], [idB, aggB]) => {
      // Primary: Majority judgment comparison
      const mjComparison = compareMajorityJudgments(aggA.majorityJudgment, aggB.majorityJudgment, gradeRankings);
      console.log(`🔍 MJ Comparison: ${idA}[${aggA.majorityJudgment.medianGrade}] vs ${idB}[${aggB.majorityJudgment.medianGrade}] → ${mjComparison} (${mjComparison < 0 ? idA : mjComparison > 0 ? idB : 'TIE'})`);
      if (mjComparison !== 0) {
        return mjComparison;
      }
      
      // Tie-breaker 1: Earlier message wins
      const indexA = getMessageIndex(idA);
      const indexB = getMessageIndex(idB);
      const indexDiff = indexA - indexB;
      if (indexDiff !== 0) {
        console.log(`🔍 Message index tie-breaker: ${idA}[${indexA}] vs ${idB}[${indexB}] → ${indexDiff} (${indexDiff < 0 ? idA : idB} wins)`);
        return indexDiff;
      }
      
      // Tie-breaker 2: For same message (ionoff vs ionoff/version), keep plain ionoff first
      if (idA === 'ionoff' && idB.startsWith('ionoff/ionoff-v')) return -1;
      if (idB === 'ionoff' && idA.startsWith('ionoff/ionoff-v')) return 1;
      
      // Tie-breaker 3: Alphabetical (for stable sort)
      return idA.localeCompare(idB);
    });
    
    console.log('🔍 aggregateByModel: Final sorted order', {
      order: aggregations.map(([id, agg]) => ({ 
        id, 
        grade: agg.majorityJudgment.medianGrade,
        messageIndex: getMessageIndex(id)
      }))
    });
    
    // Convert sorted array back to Map (preserves insertion order)
    const result = new Map<string, ModelGradeAggregation>(aggregations);
    return result;
  }
  
  /**
   * Aggregate raw grades by creator
   */
  aggregateByCreator(grades: RawGrade[]): Map<string, CreatorGradeAggregation> {
    const byCreator = new Map<string, RawGrade[]>();
    
    grades.forEach(grade => {
      const creator = grade.candidateModelId.split('/')[0];
      if (!byCreator.has(creator)) {
        byCreator.set(creator, []);
      }
      byCreator.get(creator)!.push(grade);
    });
    
    const result = new Map<string, CreatorGradeAggregation>();
    byCreator.forEach((creatorGrades, creator) => {
      // CRITICAL FIX: For M|N counting, exclude version duplicate grades
      const gradesForCounting = creatorGrades.filter(g => !g.isDuplicatedFromVariant);
      
      // Count unique prompts from non-duplicate grades only
      const uniquePromptIds = new Set(gradesForCounting.map(g => g.userPromptMessageId));
      
      // Calculate MJ using non-duplicate grades only
      const mj = this.calculateMajorityJudgment(gradesForCounting, uniquePromptIds);
      
      result.set(creator, {
        creatorPrefix: creator,
        majorityJudgment: mj,
        grades: creatorGrades, // Keep ALL grades for display
        lastUpdated: new Date(),
        prioritySetting: creatorGrades[0]?.prioritySetting || 'speed'
      });
    });
    
    return result;
  }
  
  /**
   * Normalize model ID by removing web grounding :online suffix
   * Note: Reasoning suffixes like (high), (low) are NOT removed - they are
   * part of displayedModelID and distinguish different model configurations
   */
  private normalizeModelId(modelId: string): string {
    return modelId.replace(/:online$/, '');
  }

  /**
   * Generate peer grading matrix message (table format)
   * @param grades - Raw grades to display
   * @param messages - Messages for model ordering
   * @param selfgradeEnabled - Whether self-grading is enabled (system setting)
   */
  async generatePeerGradingMatrix(grades: RawGrade[], messages: any[], selfgradeEnabled: boolean = true): Promise<string> {
    if (grades.length === 0) {
      return '';
    }
    
    // Get preloaded rankings for sorting (returns immediately from cache)
    const gradeRankings = await getGradeRankings();

    // Filter to peer grades only (not self-eval)
    const peerGrades = grades.filter(g => !g.isSelfEval);
    console.log('🔍 generatePeerGradingMatrix: Input grades', {
      totalGrades: grades.length,
      peerGrades: peerGrades.length,
      grades: peerGrades.map(g => ({
        grader: g.graderModelId,
        candidate: g.candidateModelId,
        grade: g.grade
      }))
    });
    
    if (peerGrades.length === 0) {
      return '';
    }

    // Normalize model IDs to remove API routing suffixes
    const normalizedGrades = peerGrades.map(g => ({
      ...g,
      graderModelId: this.normalizeModelId(g.graderModelId),
      candidateModelId: this.normalizeModelId(g.candidateModelId)
    }));
    
    console.log('🔍 generatePeerGradingMatrix: After normalization', {
      normalizedGrades: normalizedGrades.map(g => ({
        grader: g.graderModelId,
        candidate: g.candidateModelId,
        grade: g.grade
      }))
    });

    // Get unique models that participated in grading
    // CRITICAL: Sort by arrival order (message index), not alphabetically
    // Backend dispatches grading by arrival index, so frontend must match
    const allModels = Array.from(new Set([
      ...normalizedGrades.map(g => g.graderModelId),
      ...normalizedGrades.map(g => g.candidateModelId)
    ])).sort((a, b) => {
      const indexA = messages.findIndex((m: any) => 
        m.role === 'assistant' && m.modelUsed === a && !m.isGradingResponse && !m.isExtractionResponse && !m.isGradeMatrix
      );
      const indexB = messages.findIndex((m: any) => 
        m.role === 'assistant' && m.modelUsed === b && !m.isGradingResponse && !m.isExtractionResponse && !m.isGradeMatrix
      );
      // If model not found in messages, push to end
      const finalA = indexA === -1 ? 999999 : indexA;
      const finalB = indexB === -1 ? 999999 : indexB;
      return finalA - finalB;
    });

    // Build grading matrix: graderModel -> candidateModel -> grade[]
    // Store ALL grades per cell to detect test-retests
    const matrix = new Map<string, Map<string, string[]>>();
    normalizedGrades.forEach(grade => {
      if (!matrix.has(grade.graderModelId)) {
        matrix.set(grade.graderModelId, new Map());
      }
      if (!matrix.get(grade.graderModelId)!.has(grade.candidateModelId)) {
        matrix.get(grade.graderModelId)!.set(grade.candidateModelId, []);
      }
      matrix.get(grade.graderModelId)!.get(grade.candidateModelId)!.push(grade.grade);
    });
    
    // Detect if any cell has multiple grades (test-retests)
    let hasTestRetests = false;
    matrix.forEach(row => {
      row.forEach(grades => {
        if (grades.length > 1) {
          hasTestRetests = true;
        }
      });
    });
    
    console.log('🔍 generatePeerGradingMatrix: Test-retest detection', {
      hasTestRetests,
      totalCells: Array.from(matrix.values()).reduce((sum, row) => sum + row.size, 0)
    });

    // Get model responses for preview text
    const modelResponses = new Map<string, string>();
    messages.forEach((msg: any) => {
      // CRITICAL: Exclude grading responses, extraction messages, and grade matrix messages
      if (msg.role === 'assistant' && msg.modelUsed && msg.content && 
          !msg.isGradingResponse && !msg.isExtractionResponse && !msg.isGradeMatrix) {
        modelResponses.set(msg.modelUsed, msg.content);
      }
    });

    // Helper to get first N words
    const getFirstWords = (text: string, wordCount: number): string => {
      const words = text.trim().split(/\s+/).slice(0, wordCount);
      return words.join(' ');
    };

    // Create model index mapping (used by both matrix table and rankings)
    const modelIndex = new Map<string, number>();
    allModels.forEach((model, idx) => {
      modelIndex.set(model, idx + 1);
    });
    
    // Helper to build a matrix table (either expanded or collapsed)
    const buildMatrixTable = (
      matrix: Map<string, Map<string, string[]>>,
      mode: 'expanded' | 'collapsed',
      title: string
    ): string => {
      let tableContent = `📊 **${title}**\n\n`;

    // Build table header
      tableContent += '```\n';
      tableContent += 'Grader → \n';
      tableContent += 'Receiver ↓  ';
    allModels.forEach((_, idx) => {
        tableContent += `  ${idx + 1} `;
    });
      tableContent += '\n';
      tableContent += '           ' + '----'.repeat(allModels.length) + '\n';

    // Build table rows
    // Include self-grades when:
    // 1. AIs=2 (minimum candidates, always include self)
    // 2. selfgradeEnabled is true (system setting)
    const includesSelfGrades = allModels.length === 2 || selfgradeEnabled;
    
    allModels.forEach((receiver, rowIdx) => {
        tableContent += `      ${rowIdx + 1}    `;
      allModels.forEach(grader => {
        if (receiver === grader && !includesSelfGrades) {
          // Only exclude self-grades when AIs > 2
            tableContent += '  x ';
        } else {
            const grades = matrix.get(grader)?.get(receiver);
            if (!grades || grades.length === 0) {
              tableContent += '  - ';
            } else if (mode === 'expanded') {
              // Expanded: Sort grades by K3 rank and concatenate
              const sortedGrades = [...grades].sort((a, b) => {
                return this.getGradeRank(a) - this.getGradeRank(b);
              });
              const gradeStr = sortedGrades.join('');
              // Pad to 3 characters for alignment
              tableContent += `${gradeStr.padEnd(3, ' ')}`;
            } else {
              // Collapsed: Use K2 median resolution
              const collapsedGrade = resolveTestRetestGrades(grades);
              tableContent += `  ${collapsedGrade} `;
            }
        }
      });
        tableContent += '\n';
    });
      tableContent += '```\n\n';

    // Add key
      tableContent += '**Key:**\n';
    allModels.forEach((model, idx) => {
        tableContent += `${idx + 1}. ${model}\n`;
    });
      
      return tableContent;
    };

    // Build content - dual matrices if test-retests detected, single matrix otherwise
    let content = '';
    
    if (hasTestRetests) {
      // Test-retests detected - show both expanded and collapsed matrices
      content += buildMatrixTable(matrix, 'expanded', 'Peer Grading Matrix (test-retest expanded)');
      content += '\n';
      content += buildMatrixTable(matrix, 'collapsed', 'Peer Grading Matrix (test-retest collapsed)');
    } else {
      // No test-retests - show single matrix (backwards compatible)
      content += buildMatrixTable(matrix, 'collapsed', 'Peer Grading Matrix');
    }

    // Calculate rankings using normalized grades (with message-index tie-breaking)
    const modelGrades = this.aggregateByModel(normalizedGrades, messages, gradeRankings);
    const rankedModels = Array.from(modelGrades.entries())
      .filter(([modelId]) => allModels.includes(modelId))
      // No need to sort - aggregateByModel already sorted with MJ + message-index tie-breaking
      .map(([modelId, agg], index) => ({
        rank: index + 1,
        modelId,
        medianGrade: agg.majorityJudgment.medianGrade,
        grades: agg.grades,
        content: modelResponses.get(modelId) || modelResponses.get(`${modelId} (high)`) || ''
      }));

    content += '\n**Final Rankings:**\n';
    rankedModels.forEach(model => {
      // Get grades from consolidated (collapsed) values for rankings
      // Include self-grades when AIs=2 OR selfgradeEnabled
      const includesSelfGrades = allModels.length === 2 || selfgradeEnabled;
      const gradeArrays = allModels
        .filter(grader => includesSelfGrades || grader !== model.modelId)
        .map(grader => matrix.get(grader)?.get(model.modelId))
        .filter((g): g is string[] => !!g && g.length > 0);
      
      // Consolidate each grader's grades using K2 median
      const grades = gradeArrays.map(gradeArray => resolveTestRetestGrades(gradeArray));
      
      // Sort grades by their K3 ranking (1=best, 15=worst)
      grades.sort((a, b) => {
        const rankA = this.getGradeRank(a);
        const rankB = this.getGradeRank(b);
        return rankA - rankB;
      });
      
      const gradeLetters = grades.join(''); // No pipes - just concatenate
      const preview = getFirstWords(model.content, 5);
      const modelNum = modelIndex.get(model.modelId);
      content += `${model.rank}. [${modelNum}] ${model.modelId} ("${preview}..."): **${model.medianGrade}** (${gradeLetters})\n`;
    });

    return content;
  }

  /**
   * Generate self-eval grading matrix message (table format)
   * 
   * NOTE: Test-retest detection logic is included for defensive programming,
   * but self-eval grading is currently batch-based (all synthesis variants 
   * graded simultaneously), so test-retests should never occur in practice.
   * 
   * Reasons for including test-retest logic:
   * 1. Consistency with generatePeerGradingMatrix()
   * 2. Future-proofing if validation rounds are added to self-eval
   * 3. Defensive handling of unexpected duplicate grades (bugs, data issues)
   * 
   * Expected behavior: hasTestRetests will always be false for self-eval matrices.
   */
  async generateSelfEvalMatrix(grades: RawGrade[], messages: any[]): Promise<string> {
    if (grades.length === 0) {
      return '';
    }
    
    // Get preloaded rankings for sorting (returns immediately from cache)
    const gradeRankings = await getGradeRankings();

    // Filter to self-eval grades only
    const selfEvalGrades = grades.filter(g => g.isSelfEval);
    console.log('🔍 generateSelfEvalMatrix: Input grades', {
      totalGrades: grades.length,
      selfEvalGrades: selfEvalGrades.length,
      grades: selfEvalGrades.map(g => ({
        grader: g.graderModelId,
        candidate: g.candidateModelId,
        grade: g.grade
      }))
    });
    
    if (selfEvalGrades.length === 0) {
      return '';
    }

    // Get unique graders and candidates
    const graderModels = Array.from(new Set(selfEvalGrades.map(g => g.graderModelId))).sort();
    const candidateModels = Array.from(new Set(selfEvalGrades.map(g => g.candidateModelId))).sort();
    
    console.log('🔍 generateSelfEvalMatrix: Unique models', {
      graders: graderModels,
      candidates: candidateModels
    });

    // Build grading matrix: graderModel -> candidateModel -> grade[]
    // Store ALL grades per cell to detect test-retests
    const matrix = new Map<string, Map<string, string[]>>();
    selfEvalGrades.forEach(grade => {
      if (!matrix.has(grade.graderModelId)) {
        matrix.set(grade.graderModelId, new Map());
      }
      if (!matrix.get(grade.graderModelId)!.has(grade.candidateModelId)) {
        matrix.get(grade.graderModelId)!.set(grade.candidateModelId, []);
      }
      matrix.get(grade.graderModelId)!.get(grade.candidateModelId)!.push(grade.grade);
    });
    
    // Detect if any cell has multiple grades (test-retests)
    // NOTE: For self-eval, this should always be false since grading is batch-based.
    // Included for defensive programming and future-proofing.
    let hasTestRetests = false;
    matrix.forEach(row => {
      row.forEach(grades => {
        if (grades.length > 1) {
          hasTestRetests = true;
        }
      });
    });
    
    console.log('🔍 generateSelfEvalMatrix: Test-retest detection', {
      hasTestRetests,
      totalCells: Array.from(matrix.values()).reduce((sum, row) => sum + row.size, 0)
    });

    // Get model responses for preview text
    const modelResponses = new Map<string, string>();
    messages.forEach((msg: any) => {
      // CRITICAL: Exclude grading responses, extraction messages, and grade matrix messages
      if (msg.role === 'assistant' && msg.content && 
          !msg.isGradingResponse && !msg.isExtractionResponse && !msg.isGradeMatrix) {
        // For synthesis variants
        // (synthesisVariant stored in Firestore has hyphens, convert to underscores for modelId)
        if (msg.synthesisVariant) {
          const variantId = `ionoff_${msg.synthesisVariant.replace(/-/g, '_')}`;
          modelResponses.set(variantId, msg.content);
        }
        // For ionoff main response
        if (msg.isSynthesized) {
          modelResponses.set('ionoff', msg.content);
        }
        // For battle winner (stored as modelId)
        if (msg.modelId && !msg.isSynthesized) {
          modelResponses.set('best', msg.content);
        }
      }
    });

    // Helper to get first N words
    const getFirstWords = (text: string, wordCount: number): string => {
      const words = text.trim().split(/\s+/).slice(0, wordCount);
      return words.join(' ');
    };

    // Create index mappings (used by both matrix table and rankings)
    const graderIndex = new Map<string, number>();
    graderModels.forEach((model, idx) => {
      graderIndex.set(model, idx + 1);
    });
    
    const candidateIndex = new Map<string, number>();
    candidateModels.forEach((model, idx) => {
      candidateIndex.set(model, idx + 1);
    });
    
    // Helper to build a self-eval matrix table (either expanded or collapsed)
    const buildSelfEvalMatrixTable = (
      matrix: Map<string, Map<string, string[]>>,
      mode: 'expanded' | 'collapsed',
      title: string
    ): string => {
      let tableContent = `📊 **${title}**\n\n`;

    // Build table header
      tableContent += '```\n';
      tableContent += 'Grader → \n';
      tableContent += 'Candidate ↓  ';
    graderModels.forEach((_, idx) => {
        tableContent += `  ${idx + 1} `;
    });
      tableContent += '\n';
      tableContent += '             ' + '----'.repeat(graderModels.length) + '\n';

    // Build table rows
    candidateModels.forEach((candidate, rowIdx) => {
        tableContent += `       ${rowIdx + 1}     `;
      graderModels.forEach(grader => {
          const grades = matrix.get(grader)?.get(candidate);
          if (!grades || grades.length === 0) {
            tableContent += '  - ';
          } else if (mode === 'expanded') {
            // Expanded: Sort grades by K3 rank and concatenate
            const sortedGrades = [...grades].sort((a, b) => {
              return this.getGradeRank(a) - this.getGradeRank(b);
            });
            const gradeStr = sortedGrades.join('');
            // Pad to 3 characters for alignment
            tableContent += `${gradeStr.padEnd(3, ' ')}`;
          } else {
            // Collapsed: Use K2 median resolution
            const collapsedGrade = resolveTestRetestGrades(grades);
            tableContent += `  ${collapsedGrade} `;
          }
        });
        tableContent += '\n';
    });
      tableContent += '```\n\n';

    // Add keys
      tableContent += '**Graders:**\n';
    graderModels.forEach((model, idx) => {
        tableContent += `${idx + 1}. ${model}\n`;
    });
    
      tableContent += '\n**Candidates:**\n';
    candidateModels.forEach((model, idx) => {
      let displayName = model;
      let label = '';
      
      if (model === 'best') {
        label = ' (AI battle winner)';
      } else if (model.startsWith('ionoff_')) {
        displayName = model;
        const variant = model.replace('ionoff_', '').replace(/_/g, '-');
        label = ` (synthesis-${variant})`;
      } else if (model.startsWith('ionoff/ionoff-v')) {
        label = ' (current ionoff version)';
      }
      
        tableContent += `${idx + 1}. ${displayName}${label}\n`;
    });
      
      return tableContent;
    };

    // Build content - dual matrices if test-retests detected, single matrix otherwise
    let content = '';
    
    if (hasTestRetests) {
      // Test-retests detected - show both expanded and collapsed matrices
      content += buildSelfEvalMatrixTable(matrix, 'expanded', 'Self-Eval Grading Matrix (test-retest expanded)');
      content += '\n';
      content += buildSelfEvalMatrixTable(matrix, 'collapsed', 'Self-Eval Grading Matrix (test-retest collapsed)');
    } else {
      // No test-retests - show single matrix (backwards compatible)
      content += buildSelfEvalMatrixTable(matrix, 'collapsed', 'Self-Eval Grading Matrix');
    }

    // Calculate rankings for self-eval (with message-index tie-breaking)
    const selfEvalModelGrades = this.aggregateByModel(selfEvalGrades, messages, gradeRankings);
    const rankedModels = Array.from(selfEvalModelGrades.entries())
      .filter(([modelId]) => candidateModels.includes(modelId))
      // No need to sort - aggregateByModel already sorted with MJ + message-index tie-breaking
      .map(([modelId, agg], index) => ({
        rank: index + 1,
        modelId,
        medianGrade: agg.majorityJudgment.medianGrade,
        grades: agg.grades,
        content: modelResponses.get(modelId) || '',
        isBattleWinner: modelId === 'best',
        isIonoff: modelId.startsWith('ionoff')
      }));

    content += '\n**Final Rankings:**\n';
    rankedModels.forEach(model => {
      // Get grades from consolidated (collapsed) values for rankings
      const gradeArrays = graderModels
        .map(grader => matrix.get(grader)?.get(model.modelId))
        .filter((g): g is string[] => !!g && g.length > 0);
      
      // Consolidate each grader's grades using K2 median
      const grades = gradeArrays.map(gradeArray => resolveTestRetestGrades(gradeArray));
      
      // Sort grades by their K3 ranking (1=best, 15=worst)
      grades.sort((a, b) => {
        const rankA = this.getGradeRank(a);
        const rankB = this.getGradeRank(b);
        return rankA - rankB;
      });
      
      const gradeLetters = grades.join(''); // No pipes - just concatenate
      const preview = getFirstWords(model.content, 5);
      const candidateNum = candidateIndex.get(model.modelId);
      
      let displayName = model.modelId;
      let label = '';
      
      if (model.modelId === 'best') {
        label = ' (AI battle winner)';
      } else if (model.modelId.startsWith('ionoff_')) {
        // Use the actual model ID (e.g., "ionoff_a_final")
        displayName = model.modelId;
        const variant = model.modelId.replace('ionoff_', '').replace(/_/g, '-');
        label = ` (synthesis-${variant})`;
      } else if (model.modelId.startsWith('ionoff/ionoff-v')) {
        // This is the ionoff version (shows with logo in UI)
        label = ' (current ionoff version)';
      }
      
      content += `${model.rank}. [${candidateNum}] ${displayName}${label} ("${preview}..."): **${model.medianGrade}** (${gradeLetters})\n`;
    });

    return content;
  }

  /**
   * OLD: Generate grade matrix message content (deprecated)
   */
  async generateGradeMatrix(
    modelGrades: Map<string, ModelGradeAggregation>,
    selectedModels: string[]
  ): Promise<string> {
    // Get preloaded rankings for sorting (returns immediately from cache)
    const gradeRankings = await getGradeRankings();
    
    const lines: string[] = [];
    lines.push('# Grade Matrix\n');
    
    // Sort models by majority judgment
    const sortedModels = Array.from(modelGrades.entries())
      .filter(([modelId]) => selectedModels.includes(modelId))
      .sort(([, a], [, b]) => 
        compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment, gradeRankings)
      );
    
    if (sortedModels.length === 0) {
      lines.push('*No grades available yet*');
      return lines.join('\n');
    }
    
    // Build table
    lines.push('| Rank | Model | Median Grade | N | Grades |');
    lines.push('|------|-------|--------------|---|--------|');
    
    sortedModels.forEach(([modelId, agg], idx) => {
      const gradeStr = agg.grades
        .map(g => g.grade)
        .slice(0, 10) // Limit to first 10 for readability
        .join(', ');
      const truncated = agg.grades.length > 10 ? '...' : '';
      
      lines.push(
        `| ${idx + 1} | ${modelId} | ${agg.majorityJudgment.medianGrade} | ${agg.majorityJudgment.judgmentsNumber} | ${gradeStr}${truncated} |`
      );
    });
    
    return lines.join('\n');
  }
  
  /**
   * Duplicate self-eval grades for ionoff version traceability
   * This ensures both the synthesis variant (e.g., ionoff_plain) and the current ionoff version
   * (e.g., ionoff/ionoff-v20251110) appear in the leaderboard with identical grades
   */
  private duplicateSelfEvalGradesForIonoffVersion(selfEvalGrades: RawGrade[]): RawGrade[] {
    // EARLY EXIT: If no self-eval grades at all, skip duplication
    if (selfEvalGrades.length === 0) {
      console.log('⏭️ duplicateSelfEvalGradesForIonoffVersion: No self-eval grades - skipping');
      return selfEvalGrades;
    }
    
    // EARLY EXIT: If no ionoff candidates exist in the grades, skip duplication
    // This handles cases where:
    // 1. Self-eval is disabled (only 'best' grades exist)
    // 2. Synthesis failed (only 'best' grades exist)
    // 3. Backend didn't create synthesis for some reason
    const hasIonoffCandidate = selfEvalGrades.some(g => 
      g.candidateModelId === 'ionoff' || 
      g.candidateModelId.startsWith('ionoff_') ||
      g.candidateModelId.startsWith('ionoff-grading-') ||
      g.candidateModelId.startsWith('ionoff/ionoff-v')
    );
    
    if (!hasIonoffCandidate) {
      console.log('⏭️ duplicateSelfEvalGradesForIonoffVersion: No ionoff candidates found - skipping duplication', {
        candidateModelIds: Array.from(new Set(selfEvalGrades.map(g => g.candidateModelId)))
      });
      return selfEvalGrades;
    }
    
    // Use the current ionoff version structure
    const currentIonoffVersion = 'ionoff-grading-v1-synthesis-v1-new-plain';
    
    console.log('🔄 duplicateSelfEvalGradesForIonoffVersion:', {
      currentIonoffVersion,
      candidateModelIds: Array.from(new Set(selfEvalGrades.map(g => g.candidateModelId))),
      ionoffEqualsFlags: Array.from(new Set(selfEvalGrades.map(g => ({ id: g.candidateModelId, ionoffEquals: g.ionoffEquals }))))
    });
    
    // STEP 1: Find ALL grades with ionoffEquals=true (the final ionoff)
    const finalIonoffGrades = selfEvalGrades.filter(g => g.ionoffEquals === true);
    
    if (finalIonoffGrades.length === 0) {
      // FALLBACK: For grades written before ionoffEquals was added,
      // use ionoff_new_plain (current production variant)
      const fallbackModelId = 'ionoff_new_plain';
      console.log('  ⚠️ No ionoffEquals found, using fallback:', fallbackModelId);
      
      const fallbackGrades = selfEvalGrades.filter(g => g.candidateModelId === fallbackModelId);
      if (fallbackGrades.length > 0) {
        console.log(`  → Found ${fallbackGrades.length} fallback grades to duplicate`);
        const duplicatedGrades = fallbackGrades.map(grade => ({
          ...grade,
          gradeId: `${grade.gradeId}_ionoff_dup`,
          candidateModelId: currentIonoffVersion,
          creator: 'ionoff',
          isDuplicatedFromVariant: grade.candidateModelId
        }));
        console.log(`  ✅ Created ${duplicatedGrades.length} duplicates from fallback`);
        return [...duplicatedGrades, ...selfEvalGrades];
      } else {
        console.log('  ❌ No fallback grades found, returning original grades');
        return selfEvalGrades;
      }
    }
    
    // STEP 2: Duplicate all grades where ionoffEquals=true
    console.log(`  → Found ${finalIonoffGrades.length} grades with ionoffEquals=true`);
    
    const duplicatedGrades: RawGrade[] = finalIonoffGrades.map(grade => {
      const duplicatedGrade: RawGrade = {
        ...grade,
        gradeId: `${grade.gradeId}_ionoff_dup`,
        candidateModelId: currentIonoffVersion,
        creator: 'ionoff',
        isDuplicatedFromVariant: grade.candidateModelId
      };
      console.log('  ✅ Duplicated grade:', {
        original: grade.candidateModelId,
        duplicate: currentIonoffVersion,
        graderModelId: grade.graderModelId,
        grade: grade.grade
      });
      return duplicatedGrade;
    });
    
    console.log(`  → Created ${duplicatedGrades.length} duplicates`);
    // Return duplicates FIRST so deduplication keeps the versioned entry
    return [...duplicatedGrades, ...selfEvalGrades];
  }
  
  /**
   * Helper: Bucket grades by priority
   */
  private bucketGradesByPriority(grades: RawGrade[]): {
    free: RawGrade[];
    speed: RawGrade[];
    balance: RawGrade[];
    value: RawGrade[];
    intelligence: RawGrade[];
    custom: RawGrade[];
  } {
    const buckets = {
      free: [] as RawGrade[],
      speed: [] as RawGrade[],
      balance: [] as RawGrade[],
      value: [] as RawGrade[],
      intelligence: [] as RawGrade[],
      custom: [] as RawGrade[],
    };
    
    grades.forEach(grade => {
      const priority = (grade.prioritySetting || 'speed') as 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom';
      if (buckets[priority]) {
        buckets[priority].push(grade);
      } else {
        // Fallback to speed for unknown priorities (e.g., old 'cost' priority)
        buckets.speed.push(grade);
      }
    });
    
    return buckets;
  }
  
  /**
   * Helper: Create empty priority bucket
   */
  private createEmptyBucket(): PriorityBucket {
    return {
      modelGrades: new Map(),
      creatorGrades: new Map(),
    };
  }
  
  /**
   * Filter grades to only those from the most recent test run
   * In development, multiple test runs can create duplicate grades for the same conversation
   * Uses gradingMessageId to identify the most recent set of grading messages
   */
  private filterToMostRecentRun(grades: RawGrade[]): RawGrade[] {
    if (grades.length === 0) return grades;
    
    // Group by prompt and type (peer-review vs self-eval)
    const groupKey = (g: RawGrade) => `${g.userPromptMessageId}-${g.isSelfEval ? 'SE' : 'PR'}`;
    const groups = new Map<string, RawGrade[]>();
    
    grades.forEach(grade => {
      const key = groupKey(grade);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(grade);
    });
    
    // For each group, find the most recent grading message set
    const filtered: RawGrade[] = [];
    groups.forEach((groupGrades, key) => {
      // Find the latest timestamp
      const maxTimestamp = Math.max(...groupGrades.map(g => g.timestamp.getTime()));
      
      // Group grades by their message ID
      const messageGroups = new Map<string, RawGrade[]>();
      groupGrades.forEach(grade => {
        const msgId = grade.gradingMessageId || 'unknown';
        if (!messageGroups.has(msgId)) {
          messageGroups.set(msgId, []);
        }
        messageGroups.get(msgId)!.push(grade);
      });
      
      // Find which message IDs are within 200ms of the latest timestamp
      // This captures all messages from the final grading round (progressive grading)
      // while filtering out earlier test runs
      const TIME_TOLERANCE_MS = 200; // 200 milliseconds
      const latestMessageIds = new Set<string>();
      messageGroups.forEach((msgGrades, msgId) => {
        const msgTimestamp = msgGrades[0].timestamp.getTime();
        const timeDiff = maxTimestamp - msgTimestamp;
        if (timeDiff <= TIME_TOLERANCE_MS) {
          latestMessageIds.add(msgId);
        }
      });
      
      // Keep only grades from the most recent message IDs
      const recentGrades = groupGrades.filter(g => 
        latestMessageIds.has(g.gradingMessageId || 'unknown')
      );
      
      filtered.push(...recentGrades);
    });
    
    if (filtered.length !== grades.length) {
      console.log('🔍 filterToMostRecentRun:', {
        totalGrades: grades.length,
        filteredGrades: filtered.length,
        removedOldGrades: grades.length - filtered.length
      });
    }
    
    return filtered;
  }

  /**
   * Process all grades for a conversation
   * Main entry point for grade processing
   * @param conversationId - The conversation ID that this state belongs to
   * @param messages - Optional messages array for message-index-based tie-breaking (earlier message wins)
   * @param gradeRankings - Optional K3 rankings from Firestore (for proper sorting to match matrix)
   * @param isInitialLoad - If true, apply filterToMostRecentRun to remove progressive grading artifacts (default: false)
   */
  async processConversation(conversationId: string, rawGrades: RawGrade[], messages?: any[], gradeRankings?: Record<string, number>, isInitialLoad = false): Promise<ConversationGradeState> {
    // CRITICAL: Only filter on initial load (navigation/refresh) to remove progressive grading artifacts
    // During real-time updates, we want ALL grades because each new grade is legitimate
    const filteredGrades = isInitialLoad ? this.filterToMostRecentRun(rawGrades) : rawGrades;
    
    // Separate self-eval from peer grades
    const peerGrades = filteredGrades.filter(g => !g.isSelfEval);
    let selfEvalGrades = filteredGrades.filter(g => g.isSelfEval);
    
    // Detect potential duplicates BEFORE processing
    const gradeKeys = filteredGrades.map(g => 
      `${g.graderModelId}|${g.candidateModelId}|${g.grade}|${g.isSelfEval ? 'SE' : 'P'}`
    );
    const uniqueGradeKeys = new Set(gradeKeys);
    const hasDuplicates = gradeKeys.length > uniqueGradeKeys.size;
    
    console.log('🔍 processConversation: Grade segregation', {
      conversationId: conversationId.substring(0, 8),
      totalGrades: filteredGrades.length,
      uniqueGrades: uniqueGradeKeys.size,
      peerGrades: peerGrades.length,
      selfEvalGrades: selfEvalGrades.length,
      hasDuplicatesDetected: hasDuplicates,
      duplicateCount: hasDuplicates ? (gradeKeys.length - uniqueGradeKeys.size) : 0,
      hasGradeRankings: !!gradeRankings
    });
    
    if (hasDuplicates) {
      console.warn('⚠️ DUPLICATE GRADES DETECTED in rawGrades array before processing!', {
        totalGrades: gradeKeys.length,
        uniqueGrades: uniqueGradeKeys.size,
        duplicates: gradeKeys.length - uniqueGradeKeys.size
      });
    }
    
    // Duplicate self-eval grades for ionoff version traceability
    selfEvalGrades = this.duplicateSelfEvalGradesForIonoffVersion(selfEvalGrades);
    
    // For self-eval, get gradeRankings if not provided (needed to match matrix ordering)
    let selfEvalGradeRankings = gradeRankings;
    if (!selfEvalGradeRankings && selfEvalGrades.length > 0) {
      selfEvalGradeRankings = await getGradeRankings();
    }
    
    // STEP 1: Bucket peer grades by priority
    const peerBuckets = this.bucketGradesByPriority(peerGrades);
    const selfEvalBucketedGrades = this.bucketGradesByPriority(selfEvalGrades);
    
    // STEP 2: Aggregate each priority bucket separately
    const buckets = {
      free: this.createEmptyBucket(),
      speed: this.createEmptyBucket(),
      balance: this.createEmptyBucket(),
      value: this.createEmptyBucket(),
      intelligence: this.createEmptyBucket(),
      custom: this.createEmptyBucket(),
    };
    
    // Populate each bucket
    (['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const).forEach(priority => {
      if (peerBuckets[priority].length > 0) {
        buckets[priority].modelGrades = this.aggregateByModel(peerBuckets[priority], messages, gradeRankings);
        buckets[priority].creatorGrades = this.aggregateByCreator(peerBuckets[priority]);
      }
    });
    
    // STEP 3: Create combined bucket for "this conversation" display
    // Merge all priority buckets together
    const thisConversationCombined = {
      modelGrades: this.aggregateByModel(peerGrades, messages, gradeRankings),
      creatorGrades: this.aggregateByCreator(peerGrades),
    };
    
    // STEP 4: Process self-eval grades (bucketed by priority)
    const selfEvalGrades_bucketed = {
      free: new Map<string, ModelGradeAggregation>(),
      speed: new Map<string, ModelGradeAggregation>(),
      balance: new Map<string, ModelGradeAggregation>(),
      value: new Map<string, ModelGradeAggregation>(),
      intelligence: new Map<string, ModelGradeAggregation>(),
      custom: new Map<string, ModelGradeAggregation>(),
    };
    
    // Populate self-eval buckets
    (['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const).forEach(priority => {
      if (selfEvalBucketedGrades[priority].length > 0) {
        selfEvalGrades_bucketed[priority] = this.aggregateByModel(
          selfEvalBucketedGrades[priority], 
          messages, 
          selfEvalGradeRankings
        );
      }
    });
    
    // STEP 5: Create combined self-eval for "this conversation" display
    const selfEvalCombined = this.aggregateByModel(selfEvalGrades, messages, selfEvalGradeRankings);
    
    console.log('🔍 processConversation: Bucketing complete', {
      peerBucketCounts: {
        free: buckets.free.modelGrades.size,
        speed: buckets.speed.modelGrades.size,
        balance: buckets.balance.modelGrades.size,
        intelligence: buckets.intelligence.modelGrades.size,
        custom: buckets.custom.modelGrades.size,
      },
      combinedCount: thisConversationCombined.modelGrades.size,
      selfEvalBucketCounts: {
        free: selfEvalGrades_bucketed.free.size,
        speed: selfEvalGrades_bucketed.speed.size,
        balance: selfEvalGrades_bucketed.balance.size,
        intelligence: selfEvalGrades_bucketed.intelligence.size,
        custom: selfEvalGrades_bucketed.custom.size,
      },
      selfEvalCombinedCount: selfEvalCombined.size,
    });
    
    return {
      conversationId,
      buckets,
      thisConversationCombined,
      selfEvalGrades: selfEvalGrades_bucketed,
      selfEvalCombined,
      rawGrades,
      lastUpdated: new Date()
    };
  }
  
  /**
   * Incrementally add new grades to existing state
   * More efficient than reprocessing all grades
   * NOTE: In practice, we just reprocess all grades since bucketing logic is complex
   */
  async addGrades(
    existingState: ConversationGradeState,
    newGrades: RawGrade[],
    messages?: any[],
    gradeRankings?: Record<string, number>
  ): Promise<ConversationGradeState> {
    // CRITICAL: Deduplicate new grades before adding them
    // A grade is a duplicate if it has the same grader, candidate, prompt, and grade letter
    // This prevents processing duplicate self-eval rounds from backend bugs
    const existingGradeKeys = new Set(
      existingState.rawGrades.map(g => 
        `${g.graderModelId}|${g.candidateModelId}|${g.userPromptMessageId}|${g.grade}|${g.isSelfEval ? 'selfeval' : 'peer'}`
      )
    );
    
    const uniqueNewGrades = newGrades.filter(g => {
      const key = `${g.graderModelId}|${g.candidateModelId}|${g.userPromptMessageId}|${g.grade}|${g.isSelfEval ? 'selfeval' : 'peer'}`;
      if (existingGradeKeys.has(key)) {
        console.warn('⚠️ Duplicate grade detected and filtered:', {
          grader: g.graderModelId,
          candidate: g.candidateModelId,
          grade: g.grade,
          type: g.isSelfEval ? 'selfeval' : 'peer',
          promptId: g.userPromptMessageId?.substring(0, 8)
        });
        return false;
      }
      return true;
    });
    
    if (uniqueNewGrades.length < newGrades.length) {
      console.log('🔍 Filtered out duplicate grades:', {
        totalNew: newGrades.length,
        uniqueNew: uniqueNewGrades.length,
        duplicatesRemoved: newGrades.length - uniqueNewGrades.length
      });
    }
    
    // Combine with existing grades
    const allGrades = [...existingState.rawGrades, ...uniqueNewGrades];
    
    // Reprocess (could be optimized further, but this is fast enough)
    // Pass messages for timestamp-based tie-breaking and gradeRankings for proper sorting
    // CRITICAL: Do NOT pass isInitialLoad=true here - we want ALL grades during real-time updates
    return this.processConversation(existingState.conversationId, allGrades, messages, gradeRankings, false);
  }
}

/**
 * Singleton instance for reuse
 */
export const localGradeProcessor = new LocalGradeProcessor();

